/**
 * Shared geometry utilities for Three.js nodes
 */

import type { BufferGeometry, Mesh } from './types';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export function isMesh(obj: any): obj is Mesh {
  return obj?.isMesh === true;
}

export function isGeometry(obj: any): obj is BufferGeometry {
  return obj?.isBufferGeometry === true;
}

export function isLine(obj: any): boolean {
  return obj?.isLine === true;
}

export function extractGeometry(input: Mesh | BufferGeometry): BufferGeometry {
  if (isMesh(input)) {
    return input.geometry;
  }
  return input;
}

/**
 * Merge multiple geometries into one.
 * @param geometries - Array of geometries to merge
 * @param weldTolerance - If > 0, weld vertices within this distance
 * @returns Merged geometry, or null if empty
 */
export function mergeGeometries(
  geometries: BufferGeometry[],
  weldTolerance: number = 0
): BufferGeometry | null {
  if (geometries.length === 0) {
    return null;
  }

  let result: BufferGeometry;

  if (geometries.length === 1) {
    result = geometries[0].clone();
  } else {
    const merged = BufferGeometryUtils.mergeGeometries(geometries, false);
    if (!merged) {
      throw new Error('Failed to merge geometries - ensure all have compatible attributes');
    }
    result = merged;
  }

  // Weld vertices if tolerance > 0
  if (weldTolerance > 0) {
    result = BufferGeometryUtils.mergeVertices(result, weldTolerance);
  }

  return result;
}
